import { DialogCloseEvent } from './dialog.js';
import { clamp, preventDefault, isMobileBreakpoint, isPointWithinElement } from './utilities.js';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1.5;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

/** @type {string} Event name used by zoom-dialog to reset zoom without calling instance methods. */
export const DRAG_ZOOM_RESET_EVENT = 'drag-zoom-wrapper:reset';

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = MIN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = MIN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /** @type {Point | null} */
  #touchStartForTap = null;

  get #image() {
    return this.refs.image;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.addEventListener(DRAG_ZOOM_RESET_EVENT, this.#resetZoom, { signal: this.#controller.signal });
    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    this.addEventListener('mousedown', this.#handleMouseDown, { signal });
    window.addEventListener('mousemove', this.#handleMouseMove, { signal });
    window.addEventListener('mouseup', this.#handleMouseUp, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized) {
      this.#initEventListeners();
    }

    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      preventDefault(event);

      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      this.#startZoomGestureFromTouches(touch1, touch2);
      return;
    }

    if (touchCount !== 1) return;

    const touch = event.touches[0];
    if (!touch) return;

    if (this.#scale > MIN_ZOOM) {
      preventDefault(event);
      this.#touchStartForTap = { x: touch.clientX, y: touch.clientY };
      this.#startDragGestureFromTouch(touch);
      return;
    }

    this.#touchStartForTap = { x: touch.clientX, y: touch.clientY };
    this.#storeTapInfo(performance.now(), touch);
  };

  /**
   * Start a zoom gesture with two touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    this.#requestHighResolutionUpgrade();
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
    this.toggleAttribute('data-dragging', true);
  }

  /**
   * Start a drag gesture with a single touch
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startDragGestureFromPoint({ x: touch.clientX, y: touch.clientY });
  }

  /**
   * Start a drag gesture from a pointer position.
   * @param {{ x: number, y: number }} point
   */
  #startDragGestureFromPoint(point) {
    this.#startPosition = { x: point.x, y: point.y };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
    this.toggleAttribute('data-dragging', true);
  }

  /**
   * Store tap information for double-tap detection
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Handle double-tap zoom toggle from touch
   * @param {Touch} touch - The touch where the double-tap occurred
   */
  #handleDoubleTapFromTouch(touch) {
    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    let targetZoom;

    // If manual zoom has been used, reset to 1x
    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM; // 1x
      this.#hasManualZoom = false; // Reset the flag
      this.#translate = { x: 0, y: 0 }; // Center the image
    } else {
      // Toggle between zoom levels: 1x ↔ 1.5x
      const tolerance = 0.05; // Small tolerance for floating point comparison

      if (Math.abs(this.#scale - MIN_ZOOM) < tolerance) {
        targetZoom = this.#getTapZoomScale();
      } else {
        targetZoom = MIN_ZOOM;
      }
    }

    // If we're not going to 1x, adjust translation to center zoom on the tap point
    if (targetZoom !== MIN_ZOOM) {
      const oldScale = this.#scale;
      this.#scale = Math.min(MAX_ZOOM, targetZoom);

      // Calculate the distance from tap point to container center
      const distanceFromCenter = {
        x: touch.clientX - containerCenter.x,
        y: touch.clientY - containerCenter.y,
      };

      // Adjust translation to center zoom on the tap point
      const scaleDelta = this.#scale / oldScale - 1.0;
      this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
      this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;
    } else {
      // Going to 1x, set the scale and center the image
      this.#scale = targetZoom;
      this.#translate = { x: 0, y: 0 }; // Center the image when going to 1x
    }

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      preventDefault(event);

      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
      return;
    }

    if (touchCount === 1 && this.#isDragging) {
      preventDefault(event);

      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
      return;
    }

    if (this.#touchStartForTap && touchCount === 1) {
      const touch = event.touches[0];
      if (touch && getDistance(touch, this.#touchStartForTap) > DRAG_THRESHOLD) {
        this.#touchStartForTap = null;
      }
    }
  };

  /**
   * Process zoom gesture from touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    // Calculate midpoint directly without object allocation
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    // Calculate current distance between touches
    const currentDistance = getDistance(touch1, touch2);

    const oldScale = this.#scale;

    // Calculate and apply new scale
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    // Mark that manual zoom has been used
    this.#hasManualZoom = true;

    // Adjust translation to keep the pinch midpoint stationary
    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;

    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;

    // Calculate how the image position needs to change to keep the midpoint stationary
    const scaleDelta = this.#scale / oldScale - 1.0;

    // Apply correction to prevent zooming on the opposite side of the midpoint
    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from a touch or mouse pointer.
   * @param {Touch | MouseEvent} pointer
   */
  #processDragGesture(pointer) {
    const distance = getDistance(pointer, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    const dx = pointer.clientX - this.#startPosition.x;
    const dy = pointer.clientY - this.#startPosition.y;

    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {MouseEvent} event
   */
  #handleMouseDown = (event) => {
    if (event.button !== 0 || this.#scale <= MIN_ZOOM) return;

    event.preventDefault();
    event.stopPropagation();
    this.#startDragGestureFromPoint({ x: event.clientX, y: event.clientY });
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseMove = (event) => {
    if (!this.#isDragging) return;

    event.preventDefault();
    this.#processDragGesture(event);
  };

  #handleMouseUp = () => {
    if (!this.#isDragging) return;

    this.#isDragging = false;
    this.toggleAttribute('data-dragging', false);
    this.#requestUpdateTransform();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (this.#hasDraggedBeyondThreshold) {
      event.preventDefault();
      event.stopPropagation();
      this.#hasDraggedBeyondThreshold = false;
      return;
    }

    if (isMobileBreakpoint()) return;

    if (!isPointWithinElement(event.clientX, event.clientY, this.#image)) return;

    event.preventDefault();
    event.stopPropagation();
    this.#toggleClickZoom(event);
  };

  /**
   * Toggle zoom on desktop click.
   * @param {MouseEvent} event
   */
  #toggleClickZoom(event) {
    this.#toggleClickZoomAtPoint({ x: event.clientX, y: event.clientY });
  }

  #handleTouchEnd = (event) => {
    const touch = event.changedTouches[0];

    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.toggleAttribute('data-dragging', false);
      this.#requestUpdateTransform();
    }

    if (this.#scale > MIN_ZOOM) {
      const wasTap =
        touch &&
        this.#touchStartForTap &&
        !this.#hasDraggedBeyondThreshold &&
        getDistance(touch, this.#touchStartForTap) < DRAG_THRESHOLD &&
        isPointWithinElement(touch.clientX, touch.clientY, this.#image);

      if (wasTap) {
        this.#toggleClickZoomFromTouch(touch);
      }

      this.#touchStartForTap = null;
      return;
    }

    if (!this.#touchStartForTap) return;

    if (!touch || getDistance(touch, this.#touchStartForTap) >= DRAG_THRESHOLD) {
      this.#touchStartForTap = null;
      return;
    }

    const currentTime = performance.now();
    const timeSinceLastTap = currentTime - this.#lastTapTime;

    if (
      timeSinceLastTap < DOUBLE_TAP_DELAY &&
      this.#lastTapPosition &&
      getDistance(touch, this.#lastTapPosition) < DOUBLE_TAP_DISTANCE
    ) {
      this.#handleDoubleTapFromTouch(touch);
      this.#lastTapTime = 0;
      this.#lastTapPosition = null;
    } else {
      this.#toggleClickZoomFromTouch(touch);
      this.#storeTapInfo(currentTime, touch);
    }

    this.#touchStartForTap = null;
  };

  /**
   * Toggle zoom on mobile tap.
   * @param {Touch} touch
   */
  #toggleClickZoomFromTouch(touch) {
    this.#toggleClickZoomAtPoint({ x: touch.clientX, y: touch.clientY });
  }

  /**
   * Toggle click/tap zoom between 1x and the scale that fills the target width.
   * @param {{ x: number, y: number }} point - Pointer position in viewport coordinates.
   */
  #toggleClickZoomAtPoint(point) {
    const tolerance = 0.05;

    if (Math.abs(this.#scale - MIN_ZOOM) < tolerance) {
      this.#applyZoomAtPoint(this.#getTapZoomScale(), point);
      return;
    }

    this.#scale = MIN_ZOOM;
    this.#translate = { x: 0, y: 0 };
    this.#hasManualZoom = false;
    this.#requestUpdateTransform();
  }

  /**
   * Tap/click zoom target: fill viewport on mobile (min 1.5x), full gallery width on desktop.
   * @returns {number}
   */
  #getTapZoomScale() {
    if (isMobileBreakpoint()) {
      return this.#getMobileTapZoomScale();
    }

    return this.#getClickZoomScale();
  }

  /**
   * Mobile tap zoom: at least 1.5x, scaled to cover the viewport (width and height).
   * @returns {number}
   */
  #getMobileTapZoomScale() {
    const { actualImageWidth, actualImageHeight } = this.#getImageContentDimensions();
    if (!actualImageWidth || !actualImageHeight) return DEFAULT_ZOOM;

    const scaleX = window.innerWidth / actualImageWidth;
    const scaleY = window.innerHeight / actualImageHeight;
    const fillScale = Math.max(scaleX, scaleY);

    return clamp(Math.max(DEFAULT_ZOOM, fillScale), MIN_ZOOM, MAX_ZOOM);
  }

  /**
   * Width click zoom should fill on desktop (gallery area).
   * @returns {number}
   */
  #getClickZoomTargetWidth() {
    return this.clientWidth;
  }

  /**
   * Scale needed so the image content spans the click-zoom target width at 1x fit.
   * @returns {number}
   */
  #getClickZoomScale() {
    const { actualImageWidth } = this.#getImageContentDimensions();

    if (!actualImageWidth) return MIN_ZOOM;

    const targetWidth = this.#getClickZoomTargetWidth();
    if (!targetWidth) return MIN_ZOOM;

    return clamp(targetWidth / actualImageWidth, MIN_ZOOM, MAX_ZOOM);
  }

  /**
   * @returns {{ actualImageWidth: number, actualImageHeight: number }}
   */
  #getImageContentDimensions() {
    const wrapperRect = this.getBoundingClientRect();
    const imageElement = this.#image;

    let naturalWidth;
    let naturalHeight;

    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.width;
    }

    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth;
    let actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    return { actualImageWidth, actualImageHeight };
  }

  /**
   * Apply zoom centered on a pointer position.
   * @param {number} targetZoom
   * @param {{ x: number, y: number }} point
   */
  #applyZoomAtPoint(targetZoom, point) {
    if (targetZoom > MIN_ZOOM) {
      this.#requestHighResolutionUpgrade();
    }

    const oldScale = this.#scale;
    this.#scale = clamp(targetZoom, MIN_ZOOM, MAX_ZOOM);

    if (this.#scale <= MIN_ZOOM) {
      this.#translate = { x: 0, y: 0 };
      this.#hasManualZoom = false;
      this.#requestUpdateTransform();
      return;
    }

    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };
    const distanceFromCenter = {
      x: point.x - containerCenter.x,
      y: point.y - containerCenter.y,
    };
    const scaleDelta = this.#scale / oldScale - 1.0;
    this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    // Keep scale between MIN_ZOOM (1) and MAX_ZOOM (5)
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    // At minimum zoom (1x), the full image should be visible with no dragging allowed
    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    const wrapperRect = this.getBoundingClientRect();
    const { actualImageWidth, actualImageHeight } = this.#getImageContentDimensions();

    // Apply current zoom scale
    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;

    // SIMPLE APPROACH: Calculate constraints directly from image content dimensions
    // If image content is larger than wrapper, calculate max translation directly

    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

    // Max translation is half the overflow (since image starts centered)
    const maxTranslateX = horizontalOverflow / 2 / this.#scale;
    const maxTranslateY = verticalOverflow / 2 / this.#scale;

    // Apply symmetric constraints (object-fit: contain behavior)
    // Image starts centered, can move maxTranslate in each direction
    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);

    // Apply final transforms to CSS
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    const wasZoomed = this.hasAttribute('data-zoomed');

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);

    const isZoomed = this.#scale > MIN_ZOOM + 0.05;
    this.toggleAttribute('data-zoomed', isZoomed);

    if (!wasZoomed && isZoomed) {
      this.#notifyZoomStarted();
    } else if (wasZoomed && !isZoomed) {
      this.#notifyZoomEnded();
    }
  };

  /**
   * Reset zoom transform to the default state.
   */
  resetZoom() {
    this.#resetZoom();
  }

  /**
   * Reset zoom to default state (1x scale, centered position).
   * Used for programmatic resets (slide change, dialog close) — not user zoom-out on mobile.
   */
  #resetZoom = () => {
    this.#scale = MIN_ZOOM;
    this.#startScale = MIN_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;

    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.#hasManualZoom = false;
    this.#touchStartForTap = null;
    this.toggleAttribute('data-dragging', false);
    this.toggleAttribute('data-zoomed', false);

    this.style.setProperty('--drag-zoom-scale', MIN_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
  };

  #requestHighResolutionUpgrade() {
    const container = this.closest('.product-media-container--image');
    const zoomDialog = this.closest('zoom-dialog');

    if (!(container instanceof HTMLElement) || !(zoomDialog instanceof HTMLElement)) return;

    if (
      'upgradeHighResolutionImage' in zoomDialog &&
      typeof zoomDialog.upgradeHighResolutionImage === 'function'
    ) {
      zoomDialog.upgradeHighResolutionImage(container);
    }
  }

  #notifyZoomStarted() {
    const container = this.closest('.product-media-container');
    const zoomDialog = this.closest('zoom-dialog');

    if (!(container instanceof HTMLElement) || !(zoomDialog instanceof HTMLElement)) return;

    if (
      'lockActiveMediaForElement' in zoomDialog &&
      typeof zoomDialog.lockActiveMediaForElement === 'function'
    ) {
      zoomDialog.lockActiveMediaForElement(container);
    }
  }

  #notifyZoomEnded() {
    if (!isMobileBreakpoint()) return;

    const zoomDialog = this.closest('zoom-dialog');

    if (!(zoomDialog instanceof HTMLElement)) return;

    if (
      'restoreActiveSlideScroll' in zoomDialog &&
      typeof zoomDialog.restoreActiveSlideScroll === 'function'
    ) {
      zoomDialog.restoreActiveSlideScroll();
    }
  }

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch} point1 - First point or touch
 * @param {Point | Touch} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch objects (clientX, clientY)
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
